import { Component, OnInit, signal, computed } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TableRowSelectEvent } from 'primeng/table';
import { PaginatorState } from 'primeng/paginator';
import { WithholdingTaxProxyService } from '../../proxies/withholding-tax.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { UserAuthenticationService } from '../../services/user-authentication.service';
import { AccountsPayableRoles } from '../../models/roles/roles-group';
import {
  WithholdingBaseType,
  WithholdingCalculationTiming,
  WithholdingResidency,
  WithholdingTaxCode,
  WithholdingTaxCodeForm,
  WithholdingTaxCodeInsert,
  WithholdingTaxKind,
  WithholdingTaxType,
  WithholdingTaxTypeForm,
  WithholdingTaxTypeInsert,
} from '../../models/withholding/withholding';
import { ResponseTranslation } from '../../models/Translations';

interface Option<T> {
  label: string;
  value: T;
}

/**
 * Withholding-tax catalog admin (#4304) — manage country-keyed withholding TYPES (calculation rules) and
 * their effective-dated rate CODES. Structural slice: catalog management only, no GL/payment posting.
 */
@Component({
  selector: 'app-withholding-taxes',
  templateUrl: './withholding-taxes.component.html',
})
export class WithholdingTaxesComponent implements OnInit {
  // Enums exposed to the template
  readonly TaxKind = WithholdingTaxKind;
  readonly Residency = WithholdingResidency;

  // State
  types = signal<WithholdingTaxType[]>([]);
  typesCurrentPage = signal(1);
  typesPageSize = signal(10);
  paginatedTypes = computed(() => {
    const start = (this.typesCurrentPage() - 1) * this.typesPageSize();
    return this.types().slice(start, start + this.typesPageSize());
  });

  codes = signal<WithholdingTaxCode[]>([]);
  codesCurrentPage = signal(1);
  codesPageSize = signal(10);
  paginatedCodes = computed(() => {
    const start = (this.codesCurrentPage() - 1) * this.codesPageSize();
    return this.codes().slice(start, start + this.codesPageSize());
  });

  selectedType = signal<WithholdingTaxType | null>(null);
  loading = signal(false);

  showTypeForm = signal(false);
  editingType = signal(false);
  showCodeForm = signal(false);
  editingCode = signal(false);
  private editingCodeId: string | null = null;

  isReadOnly = false;

  // Filters
  countryFilter = new FormControl<string | null>(null);
  includeInactive = new FormControl<boolean>(true, { nonNullable: true });

  // Options
  readonly countryOptions: Option<string>[] = [
    { label: 'Honduras (HND)', value: 'HND' },
    { label: 'Guatemala (GTM)', value: 'GTM' },
    { label: 'El Salvador (SLV)', value: 'SLV' },
    { label: 'Panamá (PAN)', value: 'PAN' },
    { label: 'Costa Rica (CRI)', value: 'CRI' },
    { label: 'Nicaragua (NIC)', value: 'NIC' },
  ];
  readonly taxKindOptions: Option<WithholdingTaxKind>[] = [
    { label: 'ISR (income)', value: WithholdingTaxKind.IncomeTax },
    { label: 'IVA / ISV (VAT)', value: WithholdingTaxKind.ValueAdded },
  ];
  readonly timingOptions: Option<WithholdingCalculationTiming>[] = [
    { label: 'At payment', value: WithholdingCalculationTiming.AtPayment },
    { label: 'At invoice', value: WithholdingCalculationTiming.AtInvoice },
  ];
  readonly baseTypeOptions: Option<WithholdingBaseType>[] = [
    { label: 'Taxable subtotal (excl. tax)', value: WithholdingBaseType.TaxableSubtotal },
    { label: 'Gross total', value: WithholdingBaseType.Gross },
    { label: 'Tax amount (IVA/ISV)', value: WithholdingBaseType.TaxAmount },
  ];
  readonly residencyOptions: Option<WithholdingResidency>[] = [
    { label: 'Any', value: WithholdingResidency.Any },
    { label: 'Resident / domiciled', value: WithholdingResidency.Resident },
    { label: 'Non-resident', value: WithholdingResidency.NonResident },
  ];

  // Strict reactive forms (En/Es name + description entered separately)
  typeForm = new FormGroup<WithholdingTaxTypeForm>({
    countryCode: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    code: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    nameEn: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    nameEs: new FormControl('', { nonNullable: true }),
    descriptionEn: new FormControl('', { nonNullable: true }),
    descriptionEs: new FormControl('', { nonNullable: true }),
    taxKind: new FormControl(WithholdingTaxKind.IncomeTax, { nonNullable: true }),
    timing: new FormControl(WithholdingCalculationTiming.AtPayment, { nonNullable: true }),
    baseType: new FormControl(WithholdingBaseType.TaxableSubtotal, { nonNullable: true }),
    isActive: new FormControl(true, { nonNullable: true }),
  });

  codeForm = new FormGroup<WithholdingTaxCodeForm>({
    withholdingTypeReference: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    code: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    nameEn: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    nameEs: new FormControl('', { nonNullable: true }),
    descriptionEn: new FormControl('', { nonNullable: true }),
    descriptionEs: new FormControl('', { nonNullable: true }),
    rate: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(100)] }),
    residency: new FormControl(WithholdingResidency.Any, { nonNullable: true }),
    effectiveFrom: new FormControl<Date | null>(null, [Validators.required]),
    effectiveTo: new FormControl<Date | null>(null),
    postingAccountReference: new FormControl<string | null>(null),
    postingAccountNumber: new FormControl<string | null>(null),
    postingAccountName: new FormControl<string | null>(null),
    isActive: new FormControl(true, { nonNullable: true }),
  });

  /** Build a bilingual Translation from En/Es inputs (Es falls back to En for the required name). */
  private nameTranslation(en: string, es: string): ResponseTranslation {
    const e = en.trim();
    const s = es.trim();
    return { en: e, es: s || e };
  }

  /** Optional bilingual Description — null when both inputs are blank. */
  private descriptionTranslation(en: string, es: string): ResponseTranslation | null {
    const e = en.trim();
    const s = es.trim();
    return e || s ? { en: e, es: s } : null;
  }

  constructor(
    private proxy: WithholdingTaxProxyService,
    private userAuth: UserAuthenticationService,
  ) {
    this.isReadOnly = !this.userAuth.roles.includes(AccountsPayableRoles.AccountsPayableAdmin);
  }

  ngOnInit(): void {
    this.loadTypes();
  }

  // ---- Types ----

  loadTypes(): void {
    this.loading.set(true);
    this.proxy.getTypes(this.countryFilter.value, this.includeInactive.value).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.types.set(res.data);
          this.typesCurrentPage.set(1);
        }
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  selectType(type: WithholdingTaxType): void {
    this.selectedType.set(type);
    this.cancelCodeForm();
    this.loadCodes(type.id!);
  }

  onTypeRowSelect(event: TableRowSelectEvent): void {
    this.selectType(event.data);
  }

  onTypesPageChange(event: PaginatorState): void {
    this.typesCurrentPage.set((event.page ?? 0) + 1);
    this.typesPageSize.set(event.rows ?? this.typesPageSize());
  }

  onCodesPageChange(event: PaginatorState): void {
    this.codesCurrentPage.set((event.page ?? 0) + 1);
    this.codesPageSize.set(event.rows ?? this.codesPageSize());
  }

  addType(): void {
    this.editingType.set(false);
    this.typeForm.reset({
      countryCode: this.countryFilter.value ?? '',
      code: '',
      nameEn: '',
      nameEs: '',
      descriptionEn: '',
      descriptionEs: '',
      taxKind: WithholdingTaxKind.IncomeTax,
      timing: WithholdingCalculationTiming.AtPayment,
      baseType: WithholdingBaseType.TaxableSubtotal,
      isActive: true,
    });
    this.typeForm.controls.countryCode.enable();
    this.typeForm.controls.code.enable();
    this.showTypeForm.set(true);
  }

  editType(type: WithholdingTaxType): void {
    this.editingType.set(true);
    this.typeForm.reset({
      countryCode: type.countryCode,
      code: type.code,
      nameEn: type.name?.en ?? '',
      nameEs: type.name?.es ?? '',
      descriptionEn: type.description?.en ?? '',
      descriptionEs: type.description?.es ?? '',
      taxKind: type.taxKind,
      timing: type.timing,
      baseType: type.baseType,
      isActive: type.isActive,
    });
    // Country + code are immutable once created.
    this.typeForm.controls.countryCode.disable();
    this.typeForm.controls.code.disable();
    this.selectedType.set(type);
    this.showTypeForm.set(true);
  }

  saveType(): void {
    if (this.typeForm.invalid) {
      GlobalMessageUtil.warn('Form Invalid', 'Please fill all required fields.');
      return;
    }
    const raw = this.typeForm.getRawValue();
    const payload: WithholdingTaxTypeInsert = {
      countryCode: raw.countryCode.trim().toUpperCase(),
      code: raw.code.trim(),
      name: this.nameTranslation(raw.nameEn, raw.nameEs),
      description: this.descriptionTranslation(raw.descriptionEn, raw.descriptionEs),
      taxKind: raw.taxKind,
      timing: raw.timing,
      baseType: raw.baseType,
      isActive: raw.isActive,
    };

    const request$ =
      this.editingType() && this.selectedType()?.id
        ? this.proxy.updateType(this.selectedType()!.id!, payload)
        : this.proxy.createType(payload);

    request$.subscribe({
      next: (res) => {
        if (res.success) {
          GlobalMessageUtil.success('Saved', this.editingType() ? 'Withholding type updated.' : 'Withholding type created.');
          this.cancelTypeForm();
          this.loadTypes();
        }
      },
    });
  }

  cancelTypeForm(): void {
    this.showTypeForm.set(false);
    this.editingType.set(false);
  }

  // ---- Codes ----

  loadCodes(typeId: string): void {
    this.proxy.getCodesByType(typeId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.codes.set(res.data);
          this.codesCurrentPage.set(1);
        }
      },
    });
  }

  addCode(): void {
    const type = this.selectedType();
    if (!type?.id) return;
    this.editingCode.set(false);
    this.editingCodeId = null;
    this.codeForm.reset({
      withholdingTypeReference: type.id,
      code: '',
      nameEn: '',
      nameEs: '',
      descriptionEn: '',
      descriptionEs: '',
      rate: 0,
      residency: WithholdingResidency.Any,
      effectiveFrom: new Date(),
      effectiveTo: null,
      postingAccountReference: null,
      postingAccountNumber: '2110020000',
      postingAccountName: null,
      isActive: true,
    });
    this.codeForm.controls.code.enable();
    this.showCodeForm.set(true);
  }

  editCode(code: WithholdingTaxCode): void {
    this.editingCode.set(true);
    this.editingCodeId = code.id ?? null;
    this.codeForm.reset({
      withholdingTypeReference: code.withholdingTypeReference,
      code: code.code,
      nameEn: code.name?.en ?? '',
      nameEs: code.name?.es ?? '',
      descriptionEn: code.description?.en ?? '',
      descriptionEs: code.description?.es ?? '',
      rate: code.rate,
      residency: code.residency,
      effectiveFrom: code.effectiveFrom ? new Date(code.effectiveFrom) : null,
      effectiveTo: code.effectiveTo ? new Date(code.effectiveTo) : null,
      postingAccountReference: code.postingAccount?.reference ?? null,
      postingAccountNumber: code.postingAccount?.number ?? null,
      postingAccountName: null,
      isActive: code.isActive,
    });
    this.codeForm.controls.code.disable();
    this.showCodeForm.set(true);
  }

  saveCode(): void {
    if (this.codeForm.invalid) {
      GlobalMessageUtil.warn('Form Invalid', 'Please fill all required fields with valid values.');
      return;
    }
    const raw = this.codeForm.getRawValue();
    const postingAccount =
      raw.postingAccountReference || raw.postingAccountNumber
        ? { reference: raw.postingAccountReference, number: raw.postingAccountNumber }
        : null;
    const payload: WithholdingTaxCodeInsert = {
      withholdingTypeReference: raw.withholdingTypeReference,
      code: raw.code.trim(),
      name: this.nameTranslation(raw.nameEn, raw.nameEs),
      description: this.descriptionTranslation(raw.descriptionEn, raw.descriptionEs),
      rate: raw.rate,
      residency: raw.residency,
      effectiveFrom: this.toIsoDate(raw.effectiveFrom!),
      effectiveTo: raw.effectiveTo ? this.toIsoDate(raw.effectiveTo) : null,
      postingAccount,
      isActive: raw.isActive,
    };

    const request$ =
      this.editingCode() && this.editingCodeId ? this.proxy.updateCode(this.editingCodeId, payload) : this.proxy.createCode(payload);

    request$.subscribe({
      next: (res) => {
        if (res.success) {
          GlobalMessageUtil.success('Saved', this.editingCode() ? 'Withholding code updated.' : 'Withholding code created.');
          this.cancelCodeForm();
          if (this.selectedType()?.id) this.loadCodes(this.selectedType()!.id!);
        }
      },
    });
  }

  cancelCodeForm(): void {
    this.showCodeForm.set(false);
    this.editingCode.set(false);
    this.editingCodeId = null;
  }

  // ---- Display helpers ----

  taxKindLabel(value: WithholdingTaxKind): string {
    return this.taxKindOptions.find((o) => o.value === value)?.label ?? `${value}`;
  }

  timingLabel(value: WithholdingCalculationTiming): string {
    return this.timingOptions.find((o) => o.value === value)?.label ?? `${value}`;
  }

  baseTypeLabel(value: WithholdingBaseType): string {
    return this.baseTypeOptions.find((o) => o.value === value)?.label ?? `${value}`;
  }

  residencyLabel(value: WithholdingResidency): string {
    return this.residencyOptions.find((o) => o.value === value)?.label ?? `${value}`;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
